import React, { useState } from "react";
import api from "../lib/api";
import { LAB_CATALOG, LAB_LOOKUP, LAB_PACKAGES } from "../lib/labCatalog";
import { LabPackagePicker } from "./LabPickers";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Plus, Trash2, Save, Loader2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const emptyRow = () => ({ test_name: "", result: "", unit: "", reference_range: "" });

/**
 * ManualLabEntry — reception (and doctor) can type in lab values from a
 * paper report without uploading a PDF/image. Saves to /api/reports/manual
 * which drops the entry into the patient's Reports collection with
 * `entry_mode: manual`; the doctor's Reports panel + Investigations
 * auto-fill treat it identically to an AI-extracted upload.
 *
 * Props:
 *   patient  — {patient_id, name, ...} of the selected patient
 *   onSaved  — optional callback fired after successful save (parent can
 *              refresh a reports list, close modal, etc.)
 */
export default function ManualLabEntry({ patient, onSaved }) {
  const [open, setOpen] = useState(false);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [laboratory, setLaboratory] = useState("");
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);

  const updateRow = (i, patch) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    setRows(next);
  };

  const updateName = (i, name) => {
    // Auto-fill unit + reference range when the user picks a known catalog test.
    // Never clobber values the user has already typed themselves.
    const meta = LAB_LOOKUP[name];
    if (meta) {
      const patch = { test_name: name };
      if (!rows[i].unit) patch.unit = meta.unit || "";
      if (!rows[i].reference_range) patch.reference_range = meta.ref || "";
      updateRow(i, patch);
    } else {
      updateRow(i, { test_name: name });
    }
  };

  const addRow = () => setRows([...rows, emptyRow()]);
  const removeRow = (i) => setRows(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);

  // Lab package picker — one click loads every test in a profile (RFT, LFT,
  // Lipid, CBC, ...) with unit + ref-range pre-filled from LAB_LOOKUP. Values
  // stay blank; reception types the numbers off the paper report. Skips
  // duplicates so hitting two overlapping packages doesn't create dupes.
  const addPackage = (pkgKey) => {
    const pkg = LAB_PACKAGES.find((p) => p.key === pkgKey);
    if (!pkg) return;
    const existingNames = new Set(rows.map((r) => (r.test_name || "").trim()));
    // Only keep the seed blank row if it's empty; otherwise append to existing.
    const seed = rows.length === 1 && !rows[0].test_name.trim() && !rows[0].result.trim() ? [] : [...rows];
    const additions = pkg.tests
      .filter((name) => !existingNames.has(name))
      .map((name) => {
        const meta = LAB_LOOKUP[name] || {};
        return { test_name: name, result: "", unit: meta.unit || "", reference_range: meta.ref || "" };
      });
    if (!additions.length) {
      toast.info(`${pkg.label} — all tests already in the list`);
      return;
    }
    setRows([...seed, ...additions]);
    toast.success(`Added ${additions.length} tests from ${pkg.label}`);
  };

  const resetForm = () => {
    setRows([emptyRow()]);
    setReportDate(new Date().toISOString().slice(0, 10));
    setLaboratory("");
  };

  const save = async () => {
    if (!patient?.patient_id) { toast.error("Select a patient first"); return; }
    const clean = rows.filter((r) => r.test_name.trim() && r.result.trim());
    if (!clean.length) { toast.error("Enter at least one test name + value"); return; }
    setSaving(true);
    try {
      await api.post("/reports/manual", {
        patient_id: patient.patient_id,
        report_date: reportDate,
        laboratory: laboratory.trim(),
        category: "Laboratory Reports",
        tests: clean,
      });
      toast.success(`Saved ${clean.length} lab value${clean.length > 1 ? "s" : ""} — doctor will see these immediately`);
      resetForm();
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={!patient}
        className="w-full flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="manual-lab-entry-open"
        title={patient ? "Type lab values from a paper report" : "Select a patient first"}
      >
        <ClipboardList size={14} className="text-pink-600" />
        <span>Enter lab report manually (no file)</span>
        <span className="ml-auto text-[10px] text-slate-400">visible to doctor immediately</span>
      </button>
    );
  }

  return (
    <div className="border border-pink-200 bg-pink-50/40 rounded-md p-3 space-y-3" data-testid="manual-lab-entry">
      <div className="flex items-center justify-between">
        <div className="text-xs font-heading font-semibold text-slate-800 flex items-center gap-1.5">
          <ClipboardList size={13} className="text-pink-600" />
          Manual lab entry
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); resetForm(); }}
          className="text-[11px] text-slate-500 hover:text-slate-700"
          data-testid="manual-lab-entry-close"
        >
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Report date</label>
          <Input
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            className="h-9 mt-0.5 tabular"
            data-testid="manual-lab-date"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Laboratory</label>
          <Input
            value={laboratory}
            onChange={(e) => setLaboratory(e.target.value)}
            placeholder="e.g. Metropolis, SRL, Redcliffe"
            className="h-9 mt-0.5"
            data-testid="manual-lab-name"
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-600 font-medium">Tests</div>
        <LabPackagePicker packages={LAB_PACKAGES} onAdd={addPackage} />
      </div>

      <div className="border border-white rounded bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-slate-500 border-b border-slate-100">
              <th className="py-1 px-2 font-medium">Test</th>
              <th className="py-1 px-2 font-medium w-24">Value</th>
              <th className="py-1 px-2 font-medium w-24">Unit</th>
              <th className="py-1 px-2 font-medium w-36">Ref range</th>
              <th className="py-1 px-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-50 last:border-b-0" data-testid={`manual-lab-row-${i}`}>
                <td className="p-1">
                  <Input
                    list="manual-lab-catalog"
                    value={r.test_name}
                    onChange={(e) => updateName(i, e.target.value)}
                    placeholder="e.g. HbA1c"
                    className="h-8 text-[11px]"
                    data-testid={`manual-lab-name-${i}`}
                  />
                </td>
                <td className="p-1">
                  <Input
                    value={r.result}
                    onChange={(e) => updateRow(i, { result: e.target.value })}
                    className="h-8 text-[11px] tabular"
                    data-testid={`manual-lab-value-${i}`}
                  />
                </td>
                <td className="p-1">
                  <Input
                    value={r.unit}
                    onChange={(e) => updateRow(i, { unit: e.target.value })}
                    className="h-8 text-[11px]"
                    data-testid={`manual-lab-unit-${i}`}
                  />
                </td>
                <td className="p-1">
                  <Input
                    value={r.reference_range}
                    onChange={(e) => updateRow(i, { reference_range: e.target.value })}
                    className="h-8 text-[11px]"
                    data-testid={`manual-lab-ref-${i}`}
                  />
                </td>
                <td className="p-1 text-center">
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    disabled={rows.length === 1}
                    className="p-1 text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`manual-lab-remove-${i}`}
                    title="Remove this row"
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <datalist id="manual-lab-catalog">
          {LAB_CATALOG.flatMap((g) => g.tests).map((t) => (
            <option key={t.name} value={t.name} />
          ))}
        </datalist>
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={addRow}
          className="text-[11px] px-2 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
          data-testid="manual-lab-add-row"
        >
          <Plus size={11} /> Add another test
        </button>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-[11px] gap-1.5"
          data-testid="manual-lab-save"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save lab report
        </Button>
      </div>
    </div>
  );
}
